import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { type LayoutChangeEvent, StyleSheet, TextStyle, View, type ViewStyle } from 'react-native';
import Animated from 'react-native-reanimated';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';

import { Container, useResponsive } from '@sg/garnish';

import { LoyaltyHomeProgressBarMilestone } from './components';
import { useLoyaltyHomeProgressBarAnimation } from './hooks';

export const LoyaltyHomeProgressBar = (props: LoyaltyHomeProgressBarProps) => {
  const {
    availablePoints,
    totalPoints,
    pointsRewards,
    palette,
    customSpacing,
    barStyle,
    size = 'medium',
    compact,
    dataSet,
    onlyTerminalDots = false,
    isAnimated = true,
  } = props;
  const { formatMessage } = useIntl();

  const { match } = useResponsive();

  const containerSpacing = match([theme.spacing['6'], theme.spacing['10'], theme.spacing['30']]);

  const barSize = match([styles.barSizeXS, styles.barSizeSM]);

  const progressBarColorBase = palettesBase[palette];
  const progressBarColorPending = palettesPending[palette];
  const progressBarColorAvailable = palettesAvailable[palette];

  const [progressBarWidth, setProgressBarWidth] = useState(0);
  const fullBarStyle = useStyle(
    () => ({
      width: progressBarWidth,
      opacity: progressBarWidth > 0 ? 1 : 0,
    }),
    [progressBarWidth],
  );

  const progress = useMemo(() => {
    const lastRewardPoints = pointsRewards.at(-1)?.points ?? 0;

    return Math.round((availablePoints / lastRewardPoints) * 100);
  }, [availablePoints, pointsRewards]);

  const { availableBarStyle, pendingBarStyle } = useLoyaltyHomeProgressBarAnimation({
    progressBarWidth,
    availablePoints,
    totalPoints,
    pointsRewards,
    isAnimated,
  });

  const pendingBarStyles = [styles.pendingBar, barSize, barStyle, progressBarColorPending, pendingBarStyle]; // prettier-ignore
  const availableBarStyles = [styles.availableBar, barSize, barStyle, progressBarColorAvailable, availableBarStyle]; // prettier-ignore
  const progressBarStyles = [styles.progressBar, barSize, barStyle, progressBarColorBase];

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    setProgressBarWidth(event.nativeEvent.layout.width);
  }, []);

  return (
    <Container
      customSpacing={customSpacing ?? containerSpacing}
      accessibilityRole="progressbar"
      accessibilityLabel={formatMessage(messages.progressBar, { progress })}
      accessibilityValue={{ min: 0, max: 100, now: progress }}
      {...(dataSet ? { dataSet: dataSet } : {})}
    >
      <View>
        <Animated.View style={pendingBarStyles} />

        <Animated.View style={availableBarStyles} />

        <View style={progressBarStyles} onLayout={onLayout} />

        <View style={[styles.darkThemeBorder, barSize, barStyle, fullBarStyle]} />

        <Milestones
          availablePoints={availablePoints}
          pointsRewards={pointsRewards}
          palette={palette}
          compact={compact}
          size={size}
          onlyTerminalDots={onlyTerminalDots}
        />
      </View>
    </Container>
  );
};

const Milestones = (props: MilestonesProps) => {
  const { availablePoints, pointsRewards, palette, compact, size, onlyTerminalDots } = props;

  const { minWidth } = useResponsive();
  const textStyle = minWidth.isSM ? milestoneText[palette] : null;

  if (onlyTerminalDots) {
    const lastPointsReward = pointsRewards.at(-1);
    const lastMilestone = { id: 'last', points: lastPointsReward?.points ?? 2500 };

    return (
      <View style={styles.milestonesContainerOnlyTerminalDots}>
        <LoyaltyHomeProgressBarMilestone
          containerStyle={styles.milestoneOnlyTerminalDots}
          milestoneStyle={styles.milestoneNoDot}
          textStyle={[styles.milestoneTextFirst, textStyle]}
          palette={palette}
          availablePoints={availablePoints}
          size={size}
        />

        <LoyaltyHomeProgressBarMilestone
          containerStyle={styles.milestoneOnlyTerminalDots}
          milestoneStyle={styles.milestoneNoDot}
          textStyle={textStyle}
          palette={palette}
          availablePoints={availablePoints}
          pointsReward={lastMilestone}
          size={size}
        />
      </View>
    );
  }

  return (
    <View style={styles.milestonesContainer}>
      <LoyaltyHomeProgressBarMilestone
        palette={palette}
        availablePoints={availablePoints}
        isFluid={!compact}
        size={size}
      />

      {pointsRewards.map((pointsReward, index) => {
        return (
          <LoyaltyHomeProgressBarMilestone
            key={pointsReward.id}
            palette={palette}
            availablePoints={availablePoints}
            pointsReward={pointsReward}
            size={size}
            isFluid={true}
            index={index}
          />
        );
      })}
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  progressBar: {
    defaultMessage: `Loyalty reward progress bar: {progress}%`,
    description: 'Loyalty Home | Progress Bar | A11y',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  barSizeXS: {
    minHeight: 16,
    borderRadius: 16,
  },
  barSizeSM: {
    minHeight: 40,
    borderRadius: 40,
  },
  progressBar: {
    zIndex: 1,
    flex: 1,
    flexDirection: 'row',
  },
  pendingBar: {
    zIndex: 2,
    position: 'absolute',
  },
  availableBar: {
    zIndex: 3,
    position: 'absolute',
  },
  milestonesContainer: {
    zIndex: 4,
    flexDirection: 'row',
    gap: theme.spacing['3'],
  },
  milestonesContainerOnlyTerminalDots: {
    zIndex: 4,
    flexDirection: 'row',
    gap: theme.spacing['3'],
    justifyContent: 'space-between',
  },
  milestoneTextFirst: {
    width: '100%',
    paddingLeft: theme.spacing['1'],
    textAlign: 'left',
  },
  milestoneTextLight: {
    color: theme.colors.KALE,
    fontWeight: '700',
  },
  milestoneTextDark: {
    color: theme.colors.KALE,
    fontWeight: '700',
  },
  milestoneTextWhite: {
    color: theme.colors.WHITE,
    fontWeight: '700',
  },
  milestoneNoDot: {
    opacity: 0,
  },
  milestoneOnlyTerminalDots: {
    flex: 0,
    minWidth: 32,
  },
  darkThemeBorder: {
    position: 'absolute',
    borderColor: theme.colors.CREAM,
    borderWidth: 1,
    zIndex: 5,
  },
});

// ─── Palettes ───────────────────────────────────────────────────────────────

const palettesBase: Record<LoyaltyHomeProgressBarProps['palette'], ViewStyle> = {
  dark: {
    backgroundColor: theme.colors.BASIL,
  },
  light: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  white: {
    backgroundColor: theme.colors.OPACITY.KHAKI.LIGHTEST,
  },
};

const palettesPending: Record<LoyaltyHomeProgressBarProps['palette'], ViewStyle> = {
  dark: {
    backgroundColor: theme.colors.CREAM,
  },
  light: {
    backgroundColor: theme.colors.AVOCADO,
  },
  white: {
    backgroundColor: theme.colors.OPACITY.KHAKI.DARK,
  },
};

const palettesAvailable: Record<LoyaltyHomeProgressBarProps['palette'], ViewStyle> = {
  dark: {
    backgroundColor: theme.colors.AVOCADO,
  },
  light: {
    backgroundColor: theme.colors.DARK_BASIL,
  },
  white: {
    backgroundColor: theme.colors.WHITE,
  },
};

const milestoneText: Record<LoyaltyHomeProgressBarProps['palette'], TextStyle> = {
  dark: styles.milestoneTextDark,
  light: styles.milestoneTextLight,
  white: styles.milestoneTextWhite,
};

// ─── Types ──────────────────────────────────────────────────────────────────

type LoyaltyHomeProgressBarProps = {
  availablePoints: number;
  totalPoints: number;
  pointsRewards: readonly PointsReward[];
  customSpacing?: number;
  barStyle?: ViewStyle;
  palette: 'dark' | 'light' | 'white';
  size?: 'medium' | 'large';
  isAnimated?: boolean;
  onlyTerminalDots?: boolean;
  compact?: boolean;
  dataSet?: Record<string, string>;
};

type PointsReward = {
  id: string;
  name?: string;
  points?: number | null;
  assetUrl?: string | null;
};

type MilestonesProps = Omit<LoyaltyHomeProgressBarProps, 'dataSet' | 'isAnimated' | 'totalPoints'>;
